/**
 * Runtime resolver for product-level complementary match rules.
 * @see COMPLEMENTARY_PRODUCTS.md
 */

function normMm(value) {
  return String(value ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();
}

function variantOptions(variant) {
  if (!variant) return {};

  if (variant.options && !Array.isArray(variant.options)) {
    return variant.options;
  }

  return Object.fromEntries(
    (variant.options_with_values ?? variant.selectedOptions ?? []).map((option) =>
      option.name ? [option.name, option.value] : [option.option, option.value]
    )
  );
}

function conditionLeftValue(mainOptions, cond) {
  if (cond.equals != null && cond.equals !== '') return cond.equals;
  if (cond.main) return mainOptions[cond.main];
  return undefined;
}

function lookupMappedValue(valueMap, leftValue) {
  if (!valueMap || leftValue == null) return undefined;

  if (Object.prototype.hasOwnProperty.call(valueMap, leftValue)) {
    return valueMap[leftValue];
  }

  const leftNorm = normMm(leftValue);
  return Object.entries(valueMap).find(([key]) => normMm(key) === leftNorm)?.[1];
}

function valuesMatch(leftValue, accessoryValue, valueMap) {
  if (leftValue == null || leftValue === '') return false;

  if (valueMap) {
    const mapped = lookupMappedValue(valueMap, leftValue);
    if (mapped == null) return false;

    const allowed = Array.isArray(mapped) ? mapped : [mapped];
    return allowed.some((value) => normMm(value) === normMm(accessoryValue));
  }

  return normMm(leftValue) === normMm(accessoryValue);
}

function matchWhenConditions(match) {
  if (Array.isArray(match.when) && match.when.length) return match.when;

  if ((match.type === 'value_map' || match.type === 'option_map') && match.accessory) {
    if (match.equals != null && match.equals !== '') {
      return [
        {
          accessory: match.accessory,
          equals: match.equals,
          value_map: match.value_map ?? match.map,
        },
      ];
    }

    if (match.main) {
      return [
        {
          main: match.main,
          accessory: match.accessory,
          value_map: match.value_map ?? match.map,
        },
      ];
    }
  }

  return [];
}

function variantMatchesWhen(mainOptions, accessoryOptions, when, valueMap) {
  return (when ?? []).every((condition) => {
    const leftValue = conditionLeftValue(mainOptions, condition);
    const accessoryValue = accessoryOptions[condition.accessory];
    const map = condition.value_map ?? condition.map ?? valueMap;

    return valuesMatch(leftValue, accessoryValue, map);
  });
}

function resolveAccessoryVariants(mainVariant, accessoryVariants, rule) {
  const mainOptions = variantOptions(mainVariant);
  const { match } = rule;

  if (!match) return [];

  if (match.type === 'universal') return accessoryVariants;

  const when = matchWhenConditions(match);
  const valueMap = match.value_map ?? match.map;
  const matched = accessoryVariants.filter((variant) =>
    variantMatchesWhen(mainOptions, variantOptions(variant), when, valueMap)
  );

  if (match.accessoryVariants === 'all_matching') return matched;
  if (match.accessoryVariants === 'first_matching') return matched.slice(0, 1);

  return [];
}

function groupVariantsByProduct(resolvedGroups) {
  return resolvedGroups;
}

/**
 * Resolve specific complementary accessories for a main variant.
 *
 * @param {Object} mainVariant - Main variant with named `options` map
 * @param {Object} catalog - Parsed complementary accessories catalog
 * @returns {Array<{product: Object, variants: Object[], lockSelection: boolean, allowedVariantIds: string}>}
 */
function resolveSpecificComplementaryGroups(mainVariant, catalog) {
  if (!mainVariant || !catalog?.specificProducts?.length) {
    return [];
  }

  const rules = catalog.rules?.rules ?? {};
  const groups = [];

  for (const product of catalog.specificProducts) {
    const rule = rules[product.handle];
    if (!rule) continue;

    const matchedVariants = resolveAccessoryVariants(mainVariant, product.variants ?? [], rule);
    if (!matchedVariants.length) continue;

    const variantIds = matchedVariants.map((variant) => String(variant.id));
    const lockSelection = true;
    const allowedVariantIds = variantIds.join(',');

    const prices = matchedVariants.map((variant) => variant.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    groups.push({
      product,
      variants: matchedVariants,
      primaryVariant: matchedVariants[0],
      variantCount: matchedVariants.length,
      lockSelection,
      allowedVariantIds,
      showPriceRange: matchedVariants.length > 1 && minPrice !== maxPrice,
      minPrice,
      maxPrice,
    });
  }

  return groups;
}

function catalogUsesNewResolver(catalog) {
  if (!catalog) return false;

  return Array.isArray(catalog.specificProducts) && catalog.specificProducts.length > 0;
}

window.ComplementaryProductsResolver = {
  normMm,
  variantOptions,
  conditionLeftValue,
  variantMatchesWhen,
  resolveAccessoryVariants,
  resolveSpecificComplementaryGroups,
  catalogUsesNewResolver,
};
